import { ChangeDetectionStrategy, Component, ErrorHandler, Injector, OnDestroy, OnInit, inject, signal } from '@angular/core'

import {
  AiiaStartFetchManuallyTyojono,
  GetRawAiiaTransactionsQuery,
  GetRawAiiaTransactionsResponse,
  ViiaHaeIbanitLemonatorRequest,
  ViiaHaeIbanitLemonatorResponse,
  ViiaTransaction,
} from '@lemon/core-firebase/model/aiia'
import { Asiakas } from '../../../_jaettu-lemonator/model/asiakas'

import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from '../../../_angular/service/asiakas/asiakas.service'

import { FieldTree, form, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { BankConsentLog, BankCustomerConsent } from '@lemon/core-firebase/model/banks'
import { SahkoisenTiliotteenAlatyyppi } from '@lemon/core-firebase/model/tiliote'
import { BankConsentUriService } from '@lemon/core-firebase/service/bank-consent-uri.service'
import { BicService } from '@lemon/core/service/bic.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of as observableof, Subject } from 'rxjs'
import { map, switchMap, takeUntil, tap } from 'rxjs/operators'

interface ViewableTransaction extends ViiaTransaction {
  showDetails: boolean
}

interface RawTransactionsState extends GetRawAiiaTransactionsResponse {
  showTable: boolean
  loading: boolean
  transactions: ViewableTransaction[]
}

interface AccountSelectingInfo {
  iban: string
  bic: string
  bank: string
}

interface TilitapahtumienHakuLomake {
  iban: AccountSelectingInfo | null
  start: Date | null
  end: Date | null
}

@Component({
  selector: '[app-aiia-tilitapahtumat]',
  templateUrl: './aiia-tilitapahtumat.component.html',
  styleUrls: ['./aiia-tilitapahtumat.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AiiaTilitapahtumatComponent implements OnInit, OnDestroy {
  asiakas: Asiakas = null

  consentHtmlObservable: Observable<string>
  userLevelConsentsObservable: Observable<string>
  asiakkaanIbanitObservable: Observable<AccountSelectingInfo[]>
  private _historianMaaraSubject: BehaviorSubject<number> = new BehaviorSubject(50)
  historianMaaraObservable = this._historianMaaraSubject.asObservable()
  logsObservable: Observable<BankConsentLog[]>
  lomake: FieldTree<TilitapahtumienHakuLomake>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  rawTransactionsStateSubject = new BehaviorSubject<RawTransactionsState>({ showTable: false, loading: false, accountInfo: null, transactions: [], outcome: null })

  constructor(
    private _asiakasService: AsiakasService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dateService: DateService,
    private _formValidationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _bicService: BicService,
    private _snackbar: MatSnackBar,
    private _bankConsentUriService: BankConsentUriService,
  ) {}

  ngOnInit() {
    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(result => {
      this.asiakas = result
    })

    const endDate = new Date()
    const startDate = this._dateService.kuukaudenEnsimmainen(this._dateService.lisaaKuukausia(endDate, -2))

    this.lomake = form(
      signal<TilitapahtumienHakuLomake>({ iban: null, start: startDate, end: endDate }),
      polku => {
        required(polku.iban)
        required(polku.start)
        required(polku.end)
      },
      { injector: this._injector },
    )
    const ibanKentta = this.lomake.iban

    const bankCustomerConsentObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas?.avain) {
          return observableof<BankCustomerConsent>(null)
        }
        const uri = this._bankConsentUriService.getBankConsentUri(asiakas.avain)

        return this._firebaseLemonaid.firestoreDoc<BankCustomerConsent>(uri).listen()
      }),
    )

    this.consentHtmlObservable = bankCustomerConsentObservable.pipe(
      map(consent => {
        if (!consent) {
          return '<p>Käyttäjillä ei ole pankkiyhteysroolia</p>'
        }
        if (!consent.dialogsToShow?.length && !consent.forceDialogToShow?.length) {
          return '<p>Kaikki maksutavoissa olevat sähköiset tiliotteet toimivat.</p>'
        }
        let output = '<p>Pankkiyhteysikkuna on päällä:<br/><br/>'
        if (consent.forceDialogToShow?.length) {
          for (const d of consent.forceDialogToShow) {
            output += d + ' (pakotetusti päällä)'
          }
        }
        for (const d of consent?.dialogsToShow || []) {
          output += // '<p>' + d.dialogToShow?.toUpperCase() + ' ' +
            'Pankin tunnus ' +
            (d.providerMetadata?.aiiaMetadata?.providerId ?? d.providerId ?? 'Tuntematon') +
            ', vanhentunut ' +
            this._dateService.muotoile(this._dateService.numberTimestampToDate(d.connectionLost), 'dd.MM.yyyy HH:mm:ss') +
            '<br/>' +
            (d.ibansMissing ? d.ibansMissing?.join(', ') : '') +
            '</p>'
        }
        return output
      }),
    )

    this.asiakkaanIbanitObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return observableof<AccountSelectingInfo[]>(null as AccountSelectingInfo[])
        }
        const ibanRequestData: ViiaHaeIbanitLemonatorRequest = {
          asiakasAvain: asiakas.avain,
        }
        return this._firebaseLemonaid.functionsCall<ViiaHaeIbanitLemonatorRequest, ViiaHaeIbanitLemonatorResponse>('viiaGetCustomerIbansLemonatorProd', ibanRequestData).then(data => {
          if (!data || (data && data.e) || !data.ibanit) {
            return null
          }
          return data.ibanit
            .filter(iban => !!iban)
            .map(iban => {
              const bic = this._bicService.annaBicKoodi(iban) || 'BIC ei tiedossa'
              const bank = this._bicService.annaPankinNimi(iban) || 'Pankin nimi ei tiedossa'
              const val: AccountSelectingInfo = { iban: iban, bic: bic, bank: bank }
              return val
            })
        })
      }),
      tap(ibanit => {
        if (!ibanKentta().value() && ibanit?.length > 0) {
          asetaArvo(ibanKentta, ibanit[0])
        }
      }),
    )

    this.logsObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.historianMaaraObservable]).pipe(
      switchMap(([asiakas, historianMaara]) => {
        if (!asiakas) {
          return observableof<BankConsentLog[]>([])
        }
        const uri = this._bankConsentUriService.getBankConsentLogsCollection(asiakas.avain)
        return this._firebaseLemonaid.firestoreCollection<BankConsentLog>(uri).orderBy('changedTime', 'desc').limit(Number(historianMaara)).listen()
      }),
    )
  }

  async getRawAiiaTransactions() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.rawTransactionsStateSubject.value.loading = true
    this.rawTransactionsStateSubject.value.showTable = true
    this.rawTransactionsStateSubject.next(this.rawTransactionsStateSubject.value)

    try {
      const asiakasAvain = this.asiakas.avain

      if (!asiakasAvain) {
        throw new Error('No asiakasAvain')
      }

      const iban = this.lomake.iban().value()
      const startDate = this._dateService.dateToLocalDate(this.lomake.start().value())
      const endDate = this._dateService.dateToLocalDate(this.lomake.end().value())

      const requestPayload: GetRawAiiaTransactionsQuery = {
        asiakasAvain: asiakasAvain,
        iban: iban.iban,
        start: startDate,
        end: endDate,
      }

      const response = await this._firebaseLemonaid.functionsCall<GetRawAiiaTransactionsQuery, GetRawAiiaTransactionsResponse>('aiiaGetRawAiiaTransactions', requestPayload, { timeout: 540 * 1000 })

      this.rawTransactionsStateSubject.value.accountInfo = response.accountInfo
      this.rawTransactionsStateSubject.value.outcome = response.outcome
      this.rawTransactionsStateSubject.value.transactions = response.transactions as ViewableTransaction[]
      this.rawTransactionsStateSubject.value.loading = false
      this.rawTransactionsStateSubject.next(this.rawTransactionsStateSubject.value)

      // this._snackbar.open('Käynnistin ' + this._dateService.muotoilePaikallinenPaiva(startDate, 'fi') + ' - ' + this._dateService.muotoilePaikallinenPaiva(endDate, 'fi') + ' tilitapahtumien latauksen tililtä ' + iban, 'OK')
    } catch (err) {
      this._errorHandler.handleError(new Error('Aiia get raw transactions request failed: ' + (err?.message ?? 'unknown error')))
      this.rawTransactionsStateSubject.value.loading = false
      this.rawTransactionsStateSubject.next(this.rawTransactionsStateSubject.value)
    }
  }

  toggleShowDetails(transaction: ViewableTransaction) {
    if (transaction.showDetails) {
      delete transaction.showDetails
    } else {
      transaction.showDetails = true
    }
  }

  async startAiiaFetch() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    try {
      const asiakasAvain = this.asiakas.avain

      if (!asiakasAvain) {
        throw new Error('No asiakasAvain')
      }

      const iban = this.lomake.iban().value()

      const maksutavat = await firstValueFrom(this._asiakasService.nykyisenAsiakkaanMaksutavatObservable)
      const valitunTilinMaksutapa = maksutavat.find(maksutapa => maksutapa.sahkoisenTiliotteenTunniste === iban.iban)
      if (!valitunTilinMaksutapa || valitunTilinMaksutapa.sahkoisenTiliotteenAlatyyppi !== SahkoisenTiliotteenAlatyyppi.AIIA) {
        this._snackbar.open(`Tilin tapahtumia ei voida tuoda kirjanpitoon PSD2-kanavasta, maksutapa on konfiguroitu tyypiksi ${valitunTilinMaksutapa?.sahkoisenTiliotteenAlatyyppi}`, 'OK')
        return
      }

      const startDateNum = this._dateService.dateToNumber(this.lomake.start().value())
      const endDateNum = this._dateService.dateToNumber(this.lomake.end().value())

      const tyojonoData: AiiaStartFetchManuallyTyojono = {
        asiakasAvain: asiakasAvain,
        iban: iban.iban,
        start: startDateNum,
        end: endDateNum,
      }

      this._firebaseLemonaid.firestoreSetData(`tyojono/${asiakasAvain}/aiia-start-transactions-fetch/${this._firebaseLemonaid.firestoreCreateId()}`, tyojonoData)

      this._snackbar.open(
        'Tilin ' +
          iban.iban +
          ' tilitapahtumien synkronointi kirjanpitoon on käynnistetty aikavälillä ' +
          this._dateService.muotoileNumberPaiva(startDateNum, 'fi') +
          ' - ' +
          this._dateService.muotoileNumberPaiva(endDateNum, 'fi') +
          '.',
        'OK',
      )
    } catch (err) {
      this._errorHandler.handleError(new Error('Aiia transaction manual fetch start failed: ' + (err?.message ?? 'unknown error')))
    }
  }

  historianMaaraChanged(limit: string) {
    this._historianMaaraSubject.next(+limit)
  }

  // private _ibanValidator: ValidatorFn = (c: AbstractControl<AccountSelectingInfo>): ValidationErrors | null => {
  //   // console.log(c.value, this.ibanService.isValidIban(c.value))
  //   if (c && c.value && c.value.bank && !this._ibanService.isValidIban(c.value.bank)) {
  //     return { 'iban': true }
  //   }
  //   return null
  // }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
